import { useState, useEffect, useCallback, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import type { AnyValue } from "@/lib/rowTypes";

export interface Achievement {
  id: string;
  name: string;
  description: string | null;
  icon: string | null;
  reward_amount: number | null;
  category: string;
  rarity: string;
  sort_order: number;
}

export type AchievementStatus = "locked" | "in_progress" | "completed" | "claimed";

export interface AchievementClaimResult {
  ok: boolean;
  already_claimed: boolean;
  reward_amount: number;
  achievement_name: string;
  checking_balance?: number;
  deposited_to?: string;
  error?: string;
}

export interface TrackedAchievement extends Achievement {
  progress_current: number;
  progress_target: number;
  status: AchievementStatus;
  unlocked_at: string | null;
  reward_claimed_at: string | null;
  metadata: Record<string, AnyValue>;
  completion_pct: number;
  is_unlocked: boolean;
  is_claimable: boolean;
}

const RARITY_ORDER: Record<string, number> = {
  common: 0,
  uncommon: 1,
  rare: 2,
  epic: 3,
  legendary: 4,
};

export function useAchievements() {
  const { user } = useAuth();
  const [achievements, setAchievements] = useState<TrackedAchievement[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async (opts?: { skipLoading?: boolean }) => {
    if (!user) {
      setAchievements([]);
      setLoading(false);
      return;
    }

    if (!opts?.skipLoading) setLoading(true);
    const { error: syncError } = await supabase.rpc("sync_user_achievements");
    if (syncError) {
      console.error("sync_user_achievements error:", syncError);
    }

    const [{ data: all, error: achievementsError }, { data: progressRows, error: progressError }] = await Promise.all([
      supabase.from("achievements").select("*").order("sort_order"),
      supabase.from("achievement_progress").select("*").eq("user_id", user.id),
    ]);

    if (achievementsError) {
      console.error("achievements fetch error:", achievementsError);
    }
    if (progressError) {
      console.error("achievement_progress fetch error:", progressError);
    }

    const progressMap = new Map<string, AnyValue>(((progressRows) || []).map((row) => [row.achievement_id, row]));
    const merged = (((all) || []) as Achievement[])
      .map((achievement) => {
        const progress = progressMap.get(achievement.id);
        const progressTarget = Math.max(progress?.progress_target ?? 1, 1);
        const progressCurrent = Math.min(progress?.progress_current ?? 0, progressTarget);
        const status = (progress?.status ?? (progressCurrent > 0 ? "in_progress" : "locked")) as AchievementStatus;
        const completionPct = Math.min(100, Math.round((progressCurrent / progressTarget) * 100));

        return {
          ...achievement,
          progress_current: progressCurrent,
          progress_target: progressTarget,
          status,
          unlocked_at: progress?.unlocked_at ?? null,
          reward_claimed_at: progress?.reward_claimed_at ?? null,
          metadata: (progress?.metadata as Record<string, AnyValue>) ?? {},
          completion_pct: completionPct,
          is_unlocked: status === "completed" || status === "claimed",
          is_claimable: status === "completed",
        } satisfies TrackedAchievement;
      })
      .sort((a, b) => {
        if (a.sort_order !== b.sort_order) return a.sort_order - b.sort_order;
        return (RARITY_ORDER[a.rarity] ?? 0) - (RARITY_ORDER[b.rarity] ?? 0);
      });

    setAchievements(merged);
    setLoading(false);
  }, [user]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const claimReward = useCallback(async (achievementId: string) => {
    if (!user) {
      return {
        ok: false,
        already_claimed: false,
        reward_amount: 0,
        achievement_name: "Achievement",
        error: "You need to be signed in to claim rewards.",
      } satisfies AchievementClaimResult;
    }

    const { data, error } = await supabase.rpc("claim_achievement_reward", {
      p_achievement_id: achievementId,
    });

    if (error) {
      console.error("claim_achievement_reward error:", error);
      return {
        ok: false,
        already_claimed: false,
        reward_amount: 0,
        achievement_name: "Achievement",
        error: error.message || "Unable to claim reward.",
      } satisfies AchievementClaimResult;
    }

    await fetchAll({ skipLoading: true });
    return ((data as AnyValue) ?? {
      ok: false,
      already_claimed: false,
      reward_amount: 0,
      achievement_name: "Achievement",
      error: "No response received while claiming reward.",
    }) as AchievementClaimResult;
  }, [fetchAll, user]);

  const unlockedIds = useMemo(
    () => new Set(achievements.filter((achievement) => achievement.is_unlocked).map((achievement) => achievement.id)),
    [achievements]
  );

  const byCategory = achievements.reduce<Record<string, TrackedAchievement[]>>((acc, achievement) => {
    (acc[achievement.category] = acc[achievement.category] || []).push(achievement);
    return acc;
  }, {});

  const totalUnlocked = achievements.filter((achievement) => achievement.is_unlocked).length;
  const totalCount = achievements.length;
  const completionPct = totalCount > 0 ? Math.round((totalUnlocked / totalCount) * 100) : 0;

  const rarityStats = achievements.reduce<Record<string, { total: number; unlocked: number }>>((acc, achievement) => {
    if (!acc[achievement.rarity]) acc[achievement.rarity] = { total: 0, unlocked: 0 };
    acc[achievement.rarity].total++;
    if (achievement.is_unlocked) acc[achievement.rarity].unlocked++;
    return acc;
  }, {});

  const claimableAchievements = achievements.filter((achievement) => achievement.status === "completed");
  const inProgressAchievements = achievements.filter((achievement) => achievement.status === "in_progress");
  const completedAchievements = achievements.filter((achievement) => achievement.status === "claimed");
  const lockedAchievements = achievements.filter((achievement) => achievement.status === "locked");

  return {
    achievements,
    unlockedIds,
    byCategory,
    totalUnlocked,
    totalCount,
    completionPct,
    rarityStats,
    claimableAchievements,
    inProgressAchievements,
    completedAchievements,
    lockedAchievements,
    loading,
    claimReward,
    refetch: fetchAll,
  };
}
